import { useState, useEffect } from "react";
import { Save, Loader2, KeyRound, Mic2, Check, MessageCircle, Send, Code2, Copy, Mail, RefreshCw, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getSettings, updateSettings, sendDigestNow, changeAdminPassword, getAdminMagicLink, rotateAdminMagicLink } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsTab() {
  const [s, setS] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [twSid, setTwSid] = useState("");
  const [twToken, setTwToken] = useState("");
  const [twFrom, setTwFrom] = useState("whatsapp:+14155238886");
  const [twTo, setTwTo] = useState("");
  const [gmailUser, setGmailUser] = useState("");
  const [gmailPwd, setGmailPwd] = useState("");
  const [digestHour, setDigestHour] = useState(18);
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const d = await getSettings();
    setS(d);
    setVoiceId(d.elevenlabs_voice_id || "");
    setTwSid(d.twilio_account_sid || "");
    setTwFrom(d.twilio_whatsapp_from || "whatsapp:+14155238886");
    setTwTo(d.owner_whatsapp_to || "");
    setGmailUser(d.gmail_user || "");
    setDigestHour(d.daily_digest_hour ?? 18);
    setDigestEnabled(d.daily_digest_enabled ?? true);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        elevenlabs_voice_id: voiceId,
        twilio_account_sid: twSid,
        twilio_whatsapp_from: twFrom,
        owner_whatsapp_to: twTo,
        daily_digest_hour: parseInt(digestHour),
        daily_digest_enabled: digestEnabled,
        gmail_user: gmailUser,
      };
      if (apiKey) payload.elevenlabs_api_key = apiKey;
      if (twToken) payload.twilio_auth_token = twToken;
      if (gmailPwd) payload.gmail_app_password = gmailPwd;
      await updateSettings(payload);
      toast.success("Ayarlar kaydedildi");
      setApiKey(""); setTwToken(""); setGmailPwd("");
      load();
    } catch { toast.error("Hata"); } finally { setSaving(false); }
  };

  const testWhatsApp = async () => {
    setTesting(true);
    try {
      const r = await sendDigestNow();
      if (r.delivery?.ok) {
        toast.success("Test mesajı WhatsApp'a gönderildi ✓");
      } else {
        toast.error("Gönderim başarısız: " + (r.delivery?.error || "bilinmeyen hata"));
      }
    } catch (e) {
      toast.error("Hata: " + (e.response?.data?.detail || e.message));
    } finally { setTesting(false); }
  };

  if (!s) return <Loader2 className="w-5 h-5 animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      {/* ElevenLabs */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <Mic2 className="w-4 h-4" /> ElevenLabs Ses Klonu
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            ElevenLabs hesabınızdan API anahtarınızı ve klonladığınız sesin Voice ID'sini ekleyin.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1 flex items-center gap-2">
              API Key
              {s.has_elevenlabs && <Badge style={{ background: "#8B9B88" }}><Check className="w-3 h-3 mr-1" /> aktif ({s.elevenlabs_api_key_masked})</Badge>}
            </div>
            <Input
              data-testid="elevenlabs-key"
              type="password"
              placeholder={s.has_elevenlabs ? "Boş bırakırsanız korunur" : "sk_..."}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline mt-1 inline-block">
              ElevenLabs API anahtarı al →
            </a>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Voice ID</div>
            <Input data-testid="elevenlabs-voice" placeholder="bPztQ0njY2iHISIZ7CPI" value={voiceId} onChange={(e) => setVoiceId(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {/* Gmail SMTP */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <Mail className="w-4 h-4" /> Gmail Email Bildirimleri
            {s.has_gmail && <Badge style={{ background: "#8B9B88" }}><Check className="w-3 h-3 mr-1" /> aktif</Badge>}
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Yeni lead'ler ve günlük raporlar için Gmail SMTP üzerinden email gönderir. Kullanmak için Gmail
            "Uygulama Şifresi" gerekli (normal Gmail şifresi DEĞİL).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Gmail Adresi (gönderici)</Label>
            <Input data-testid="gmail-user" type="email" placeholder="drhuseyintirman@gmail.com" value={gmailUser} onChange={(e) => setGmailUser(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-2">
              Gmail Uygulama Şifresi (16 haneli)
              {s.has_gmail && <Badge variant="secondary" className="text-[10px]">kayıtlı</Badge>}
            </Label>
            <Input
              data-testid="gmail-pwd"
              type="password"
              placeholder={s.has_gmail ? "Boş bırakırsanız korunur" : "xxxx xxxx xxxx xxxx"}
              value={gmailPwd}
              onChange={(e) => setGmailPwd(e.target.value)}
            />
          </div>
          <div className="bg-secondary/40 rounded-xl p-4 text-xs text-muted-foreground space-y-2">
            <p><strong>Uygulama şifresi nasıl alınır:</strong></p>
            <ol className="list-decimal list-inside space-y-1 ml-1">
              <li><a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="text-primary hover:underline">Google Hesap Güvenlik</a> sayfasına gidin</li>
              <li>"2 Adımlı Doğrulama"yı açın (zorunlu)</li>
              <li><a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-primary hover:underline">Uygulama şifreleri</a> sayfasından "Posta" için yeni şifre oluşturun</li>
              <li>16 haneli kodu yapıştırıp kaydedin</li>
            </ol>
            <p className="pt-1"><strong>Limit:</strong> Gmail SMTP günde ~500 mail gönderebilir (ücretsiz). Klinik için fazlasıyla yeterli.</p>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp Business via Twilio */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2">
            <MessageCircle className="w-4 h-4" /> WhatsApp Bildirim (Twilio)
            {s.has_twilio && <Badge style={{ background: "#8B9B88" }}><Check className="w-3 h-3 mr-1" /> aktif</Badge>}
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Günlük lead özeti, reddetme sebepleri ve diğer bildirimler için Twilio üzerinden WhatsApp.
            <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-primary hover:underline ml-1">Twilio Console →</a>
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Account SID (AC...)</div>
              <Input data-testid="tw-sid" placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" value={twSid} onChange={(e) => setTwSid(e.target.value)} />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1 flex items-center gap-2">
                Auth Token
                {s.twilio_auth_token_masked && <Badge variant="secondary" className="text-[10px]">{s.twilio_auth_token_masked}</Badge>}
              </div>
              <Input data-testid="tw-token" type="password" placeholder={s.twilio_auth_token_masked ? "Boş bırakırsanız korunur" : "•••••"} value={twToken} onChange={(e) => setTwToken(e.target.value)} />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Gönderen WhatsApp (Twilio sandbox = +1 415 523 8886)</div>
              <Input data-testid="tw-from" placeholder="whatsapp:+14155238886" value={twFrom} onChange={(e) => setTwFrom(e.target.value)} />
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Mesajı alacak numara (sizin WhatsApp)</div>
              <Input data-testid="tw-to" placeholder="whatsapp:+905422313748" value={twTo} onChange={(e) => setTwTo(e.target.value)} />
            </div>
          </div>
          <div className="bg-secondary/40 rounded-xl p-4 text-xs text-muted-foreground space-y-1">
            <p><strong>Sandbox kullanımı:</strong> Twilio Console → Messaging → Try it out → Send a WhatsApp message. Telefonunuzdan +1 (415) 523 8886 numarasına Twilio'nun verdiği "join &lt;kelime&gt;" mesajını gönderin. Sonra yukarıdaki "Mesajı alacak numara" alanına kendi numaranızı `whatsapp:+90...` formatında girin.</p>
            <p><strong>Production:</strong> Onaylı WhatsApp Business numaranız varsa "Gönderen WhatsApp" alanını onunla değiştirin.</p>
          </div>
        </CardContent>
      </Card>

      {/* Daily digest */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2"><Send className="w-4 h-4" /> Günlük Özet Zamanlaması</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Her gün belirlenen saatte WhatsApp'ınıza günlük özet (yeni lead'ler, reddetme sebepleri, ziyaret sayısı) yollanır.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-xl">
            <div>
              <div className="font-medium text-sm">Otomatik gönderim</div>
              <div className="text-xs text-muted-foreground">Açıkken her gün belirlenen saatte tetiklenir</div>
            </div>
            <Switch data-testid="digest-toggle" checked={digestEnabled} onCheckedChange={setDigestEnabled} />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Gönderim saati (UTC) — TSİ için 3 saat ekleyin</div>
            <Select value={String(digestHour)} onValueChange={(v) => setDigestHour(v)}>
              <SelectTrigger data-testid="digest-hour"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {Array.from({ length: 24 }).map((_, h) => (
                  <SelectItem key={h} value={String(h)}>
                    {String(h).padStart(2, "0")}:00 UTC ({String((h + 3) % 24).padStart(2, "0")}:00 TSİ)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button data-testid="settings-save" onClick={save} disabled={saving} style={{ background: "#B39B7A" }} className="flex-1">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Kaydet
            </Button>
            <Button
              data-testid="digest-test"
              onClick={testWhatsApp}
              disabled={testing || !s.has_twilio}
              variant="outline"
              className="flex-1"
              title={!s.has_twilio ? "Önce Twilio bilgilerini kaydedin" : ""}
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Test Gönderimi
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2"><KeyRound className="w-4 h-4" /> Sistem</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Sohbet motoru</span><span className="font-medium">Gemini 3 Flash</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Konuşma → Yazı</span><span className="font-medium">OpenAI Whisper</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Ses sentezi</span><span className="font-medium">{s.has_elevenlabs ? "ElevenLabs (klon)" : "Tarayıcı (yedek)"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">WhatsApp</span><span className="font-medium">{s.has_twilio ? "Twilio aktif" : "kurulmadı"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Diller</span><span className="font-medium">TR · EN · AR · RU</span></div>
        </CardContent>
      </Card>

      {/* Embed snippet */}
      <Card data-testid="embed-card">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2"><Code2 className="w-4 h-4" /> Web Sitesine Yapıştır (Embed)</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Aşağıdaki kodu drhuseyintirman.com'un &lt;/body&gt; etiketinden hemen önce yapıştırın (WordPress'te
            "Tema → Tema Düzenleyici → footer.php" veya "Header Footer Code Manager" eklentisi).
            Widget, kendi sitenizde sağ alt köşede yüzen orb olarak çıkar — yeni sayfa açmaz.
          </p>
        </CardHeader>
        <CardContent>
          <EmbedCodeBox backendUrl={BACKEND_URL} />
        </CardContent>
      </Card>
    </div>
  );
}

function EmbedCodeBox({ backendUrl }) {
  const [copied, setCopied] = useState(false);
  const code = `<!-- Dr. Tırman AI Asistan -->
<script>
(function(){
  var iframe = document.createElement('iframe');
  iframe.src = '${backendUrl}/embed';
  iframe.style.cssText = 'position:fixed;bottom:0;right:0;width:480px;height:720px;max-width:100vw;max-height:100vh;border:none;z-index:2147483647;background:transparent;pointer-events:auto;';
  iframe.allow = 'microphone; autoplay';
  iframe.title = 'AI Asistan';
  document.body.appendChild(iframe);
  // Allow clicks through transparent areas
  window.addEventListener('message', function(e){
    if(e.data && e.data.type === 'drtirman-resize'){
      iframe.style.width = e.data.width + 'px';
      iframe.style.height = e.data.height + 'px';
    }
  });
})();
</script>`;

  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Kopyalandı");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      <pre className="bg-secondary/60 p-4 rounded-xl text-xs overflow-x-auto font-mono leading-relaxed border border-border/40 max-h-96">
        <code>{code}</code>
      </pre>
      <div className="flex gap-2">
        <Button data-testid="embed-copy" onClick={copy} variant="outline" size="sm">
          {copied ? <Check className="w-3 h-3 mr-2" /> : <Copy className="w-3 h-3 mr-2" />}
          {copied ? "Kopyalandı" : "Kodu Kopyala"}
        </Button>
        <a
          href={`${backendUrl}/embed`}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-primary hover:underline self-center"
        >
          Embed sayfasını önizle →
        </a>
      </div>
      <div className="bg-secondary/40 rounded-xl p-3 text-xs text-muted-foreground">
        <strong>WordPress için:</strong> Tema Editör → footer.php → &lt;/body&gt; etiketinden ÖNCE yapıştırın.
        Veya "Insert Headers and Footers" / "Header Footer Code Manager" eklentilerini kullanabilirsiniz (daha güvenli).
      </div>

      <SecurityCard />
    </div>
  );
}

function SecurityCard() {
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [magicLink, setMagicLink] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);
  const [rotating, setRotating] = useState(false);

  const loadMagicLink = async () => {
    setLinkLoading(true);
    try {
      const r = await getAdminMagicLink();
      setMagicLink(r.magic_link || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Magic link alınamadı");
    } finally { setLinkLoading(false); }
  };
  useEffect(() => { loadMagicLink(); }, []);

  const savePassword = async (e) => {
    e?.preventDefault?.();
    if (pwd.length < 8) return toast.error("Şifre en az 8 karakter olmalı");
    if (pwd !== pwd2) return toast.error("İki şifre aynı değil");
    setSavingPwd(true);
    try {
      await changeAdminPassword(pwd);
      toast.success("Şifre güncellendi. Bir sonraki girişte yeni şifre geçerli olacak.");
      setPwd(""); setPwd2("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Şifre değiştirilemedi");
    } finally { setSavingPwd(false); }
  };

  const rotate = async () => {
    if (!window.confirm("Mevcut magic link'i iptal edip yenisini oluşturayım mı? Eski link artık çalışmayacak.")) return;
    setRotating(true);
    try {
      const r = await rotateAdminMagicLink();
      setMagicLink(r.magic_link || "");
      toast.success("Yeni link oluşturuldu. Eski linkler artık çalışmaz.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link yenilenemedi");
    } finally { setRotating(false); }
  };

  const copyLink = () => {
    if (!magicLink) return;
    try {
      navigator.clipboard.writeText(magicLink);
      toast.success("Link panoya kopyalandı");
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  return (
    <Card data-testid="security-card" className="border-primary/30">
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" /> Güvenlik
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Password change */}
        <form onSubmit={savePassword} className="space-y-3" data-testid="change-password-form">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-display flex items-center gap-2">
            <KeyRound className="w-3.5 h-3.5" /> Şifre Değiştir
          </div>
          <Input
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="Yeni şifre (en az 8 karakter)"
            autoComplete="new-password"
            data-testid="new-password-input"
          />
          <Input
            type="password"
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            placeholder="Yeni şifre (tekrar)"
            autoComplete="new-password"
            data-testid="new-password-confirm"
          />
          <Button
            type="submit"
            disabled={savingPwd}
            data-testid="save-password-btn"
            className="font-display"
            style={{ background: "#2C3531" }}
          >
            {savingPwd ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Şifreyi Güncelle
          </Button>
          <p className="text-xs text-muted-foreground">
            Şifre tüm yetkili email'ler için geçerlidir. Yeni şifre kaydedildikten sonra bir sonraki girişte aktif olur.
          </p>
        </form>

        {/* Magic link management */}
        <div className="space-y-3 pt-6 border-t border-border/40">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-display flex items-center gap-2">
            <Send className="w-3.5 h-3.5" /> Telegram Tek-Tık Linki
          </div>
          <div className="bg-secondary/40 rounded-xl p-3 font-mono text-xs break-all" data-testid="magic-link-display">
            {linkLoading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : (magicLink || "Henüz oluşturulmadı")}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copyLink}
              disabled={!magicLink}
              data-testid="copy-magic-link"
            >
              <Copy className="w-4 h-4 mr-2" /> Kopyala
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={rotate}
              disabled={rotating}
              data-testid="rotate-magic-link"
              className="text-destructive border-destructive/40 hover:bg-destructive/10"
            >
              {rotating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Yeni Link Oluştur (eskisi iptal)
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Bu link her gün Telegram raporu ile size otomatik gelir. 30 günde bir otomatik yenilenir.
            Eğer link bir yere düştü/şüpheliyse "Yeni Link Oluştur"a basın — eski link anında geçersiz olur.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
